import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  FaSync, FaUserGraduate, FaEnvelope, FaFileAlt,
  FaExclamationTriangle, FaDownload, FaSearch,
  FaArrowLeft, FaLock, FaUser, FaShieldAlt, FaSignOutAlt,
  FaPaperclip, FaCheckCircle
} from 'react-icons/fa';
import { TimeContext } from '../TimeContext';
import './Homepage.css';

/* ── Constants (unchanged) ── */
const SESSION_TIMEOUT = 15 * 60 * 1000;
const SESSION_WARNING_TIME = 13 * 60 * 1000;
const LOCKOUT_TIME = 5 * 60 * 1000;
const MAX_ATTEMPTS = 5;

/* ─── Inline styles (STTC brand) ─── */
const S = {
  page:       { minHeight: '100vh', fontFamily: "'Poppins',sans-serif", background: '#EEF1FC' },
  loginWrap:  { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: 'linear-gradient(135deg, #1E3A8A 0%, #4169E1 100%)', position: 'relative', overflow: 'hidden' },
  loginCard:  { background: '#fff', borderRadius: 16, padding: '44px 40px', maxWidth: 440, width: '100%', boxShadow: '0 32px 80px rgba(0,0,0,0.25)', position: 'relative', zIndex: 2 },
  inputWrap:  { position: 'relative', marginBottom: 18 },
  inputLabel: { display: 'block', fontSize: 11, fontWeight: 700, color: '#4169E1', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 7 },
  inputField: { width: '100%', padding: '11px 16px 11px 42px', border: '1.5px solid rgba(65,105,225,0.2)', borderRadius: 8, fontFamily: "'Poppins',sans-serif", fontSize: 13, color: '#1E3A8A', background: '#F8FAFF', transition: 'all 0.3s', boxSizing: 'border-box' },
  inputIcon:  { position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF', fontSize: 13 },
  dashHeader: { background: 'linear-gradient(135deg, #1E3A8A, #4169E1)', padding: '28px 36px' },
  tabBar:     { background: '#fff', borderBottom: '1px solid rgba(65,105,225,0.1)', padding: '0 36px', display: 'flex', gap: 4, overflowX: 'auto' },
  tabBtn:     (active) => ({ padding: '16px 20px', fontFamily: "'Poppins',sans-serif", fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, transition: 'all 0.25s', borderBottom: active ? '2.5px solid #FBBF24' : '2.5px solid transparent', background: active ? 'rgba(251,191,36,0.06)' : 'transparent', color: active ? '#1E3A8A' : '#6B7280', letterSpacing: '0.04em' }),
  /* Header was background:'transparent', which let whatever sat behind the
     table show through while the text stayed near-white. Now explicit. */
  th:         { padding: '13px 18px', fontSize: 10, fontWeight: 700, color: '#FFFFFF', letterSpacing: '0.12em', textTransform: 'uppercase', textAlign: 'left', whiteSpace: 'nowrap', background: '#1E3A8A' },

  /* Every cell states its own background and colour.
     Homepage.css only styles even rows:
         .admin-table tr:nth-child(even) td { background: var(--gray-50); }
     so odd rows were transparent and showed whatever was behind them —
     the enrollment table's navy gradient, or a browser forcing dark mode —
     while this colour stayed dark grey. That is the unreadable black.
     Inline styles beat the stylesheet, so nothing can show through now. */
  td:         { padding: '13px 18px', fontSize: 13, color: '#374151', borderBottom: '1px solid rgba(65,105,225,0.06)', whiteSpace: 'nowrap', background: '#FFFFFF' },
  badge:      (color) => ({ display: 'inline-block', padding: '3px 10px', borderRadius: 50, fontSize: 11, fontWeight: 600, background: color + '18', color: color, letterSpacing: '0.04em' }),
};

/* ─── Stat Card ─── */
const StatCard = ({ icon, label, value, color }) => (
  <div style={{ background: '#fff', borderRadius: 12, padding: '22px 24px', display: 'flex', alignItems: 'center', gap: 16, border: '1px solid rgba(65,105,225,0.08)', boxShadow: '0 2px 12px rgba(65,105,225,0.05)', flex: 1, minWidth: 160 }}>
    <div style={{ width: 48, height: 48, borderRadius: 12, background: color + '14', display: 'flex', alignItems: 'center', justifyContent: 'center', color, fontSize: 18, flexShrink: 0 }}>
      {icon}
    </div>
    <div>
      <div style={{ fontFamily: "'Fraunces',serif", fontSize: 26, fontWeight: 900, color: '#1E3A8A', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4, letterSpacing: '0.06em', fontWeight: 500 }}>{label}</div>
    </div>
  </div>
);

/* ════════════════════════════════════════════
   ADMIN COMPONENT
════════════════════════════════════════════ */
/* Alternating rows, stated explicitly rather than left to the stylesheet.
   Contrast checked: #374151 on #F4F7FF is about 9.2:1, comfortably past
   WCAG AA. The muted variant (#6B7280) is about 5.2:1, still past AA. */
const ROW_BG = ['#FFFFFF', '#F4F7FF'];
const ROW_HOVER = '#ECF1FF';

const cell = (S, index, hovered, extra = {}) => ({
  ...S.td,
  background: hovered ? ROW_HOVER : ROW_BG[index % 2],
  ...extra,
});

const Admin = () => {
  const { t } = useTranslation();
  const currentTime = useContext(TimeContext);
  const navigate = useNavigate();

  /* ── State (all unchanged logic) ── */
  const [activeTab, setActiveTab] = useState('enrollment');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [lockTime, setLockTime] = useState(null);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const usernameInputRef = useRef(null);

  /* Use the same variable as the rest of the app so Admin can never end up
     pointing at a different backend than the public site. The hostname check
     is kept only as a fallback for local development. */
  const API_URL = process.env.REACT_APP_API_URL
    || (window.location.origin.includes('localhost')
      ? 'http://localhost:5000'
      : 'https://santomas-backend.up.railway.app');

  const tabs = [
    { id: 'enrollment',       label: t('admin.enrollment', 'Enrollment Applications'), icon: <FaFileAlt /> },
    { id: 'express-interest', label: t('admin.interest',   'Express Interest'),         icon: <FaEnvelope /> },
    { id: 'alumni-newsletter',label: t('admin.alumni',     'Alumni Newsletter'),        icon: <FaUserGraduate /> },
  ];

  const resetInactivityTimer = useCallback(() => {
    setLastActivity(Date.now());
    setShowSessionWarning(false);
  }, []);

  const handleLogout = useCallback(() => {
    setIsAuthenticated(false);
    setUsername(''); setPassword('');
    setLoginAttempts(0); setIsLocked(false); setLockTime(null); setShowSessionWarning(false);
    localStorage.removeItem('jwtToken');
    navigate('/');
  }, [navigate]);

  useEffect(() => {
    if (!isLocked && usernameInputRef.current) usernameInputRef.current.focus();
  }, [isLocked]);

  /* Documents used to be plain <a href> links to a public /Uploads path.
     They are now behind an authenticated endpoint, and a plain link cannot
     send an Authorization header — so fetch the file as a blob and open
     that instead. */
  const [hoveredRow, setHoveredRow] = useState(null);
  const [docBusy, setDocBusy] = useState(null);
  const [docError, setDocError] = useState(null);

  const openDocument = useCallback(async (filename, label) => {
    if (!filename) return;
    setDocBusy(filename); setDocError(null);
    try {
      const token = localStorage.getItem('jwtToken');
      if (!token) throw new Error('No access token found');

      const response = await axios.get(
        `${API_URL}/api/documents/${encodeURIComponent(filename)}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(response.data);
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (!win) {
        /* Popup blocked — fall back to a download so the click isn't wasted. */
        const a = document.createElement('a');
        a.href = url; a.download = filename;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
      }
      /* Release the blob once the browser has had time to load it. */
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      const status = err.response?.status;
      if (status === 410) {
        setDocError(`${label}: this document is no longer stored on the server. It was lost before persistent storage was configured, and the applicant will need to resend it.`);
      } else if (status === 404) {
        setDocError(`${label}: not found on the server.`);
      } else if (status === 401 || status === 403) {
        setDocError('Your session expired. Please log in again.');
        handleLogout();
      } else {
        setDocError(`${label}: could not be opened (${err.message}).`);
      }
    } finally {
      setDocBusy(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_URL]);

  const fetchData = useCallback(async (endpoint) => {
    setLoading(true); setError(null);
    try {
      const token = localStorage.getItem('jwtToken');
      if (!token) throw new Error('No access token found');
      const timestamp = new Date().getTime();
      const response = await axios.get(`${API_URL}/api/${endpoint}?_=${timestamp}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
      setLoading(false);
    } catch (err) {
      if (err.message === 'No access token found') {
        setError(t('admin.authError', 'Please log in to access this data'));
        handleLogout();
      } else {
        const msg = err.response?.data?.message
          ? `${err.response.data.message}${err.response.data.code ? ` (Code: ${err.response.data.code})` : ''}`
          : err.message || t('admin.fetchError', 'Failed to fetch data');
        setError(msg);
        setLoading(false);
      }
    }
  }, [API_URL, t, handleLogout]);

  useEffect(() => { if (isAuthenticated) fetchData(activeTab); }, [activeTab, refreshCounter, fetchData, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const events = ['mousemove', 'keydown', 'click', 'scroll'];
    const handle = () => resetInactivityTimer();
    events.forEach(e => window.addEventListener(e, handle));
    const interval = setInterval(() => {
      const elapsed = Date.now() - lastActivity;
      if (elapsed > SESSION_WARNING_TIME && !showSessionWarning) setShowSessionWarning(true);
      if (elapsed > SESSION_TIMEOUT) handleLogout();
    }, 1000);
    return () => { events.forEach(e => window.removeEventListener(e, handle)); clearInterval(interval); };
  }, [isAuthenticated, lastActivity, resetInactivityTimer, showSessionWarning, handleLogout]);

  useEffect(() => {
    if (!isLocked) return;
    const timer = setTimeout(() => { setIsLocked(false); setLoginAttempts(0); setLockTime(null); }, LOCKOUT_TIME);
    return () => clearTimeout(timer);
  }, [isLocked]);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (isLocked) {
      setPasswordError(t('admin.lockedOut', { minutes: Math.ceil((lockTime - Date.now()) / 1000 / 60), defaultValue: `Too many attempts. Try again in ${Math.ceil((lockTime - Date.now()) / 1000 / 60)} minutes` }));
      return;
    }
    setPasswordError('');
    setLoginAttempts(0); setIsLocked(false); setLockTime(null);
    try {
      const response = await axios.post(`${API_URL}/api/admin/login`,
        { username: username.trim(), password },
        { headers: { 'Content-Type': 'application/json' } }
      );
      localStorage.setItem('jwtToken', response.data.token);
      setIsAuthenticated(true);
      setPassword(''); setUsername(''); setLoginAttempts(0);
      resetInactivityTimer();
      fetchData(activeTab);
    } catch (err) {
      const msg = err.response?.data?.message || t('admin.loginError', 'Login failed. Please try again.');
      setPasswordError(msg);
      setLoginAttempts(prev => {
        const next = prev + 1;
        if (next >= MAX_ATTEMPTS) { setIsLocked(true); setLockTime(Date.now() + LOCKOUT_TIME); }
        return next;
      });
    }
  };

  const formatDate = (d) => {
    if (!d || isNaN(new Date(d))) return t('admin.noDate', 'No date');
    return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const filterData = () => {
    if (!searchTerm) return data;
    return data.filter(item => Object.values(item).join(' ').toLowerCase().includes(searchTerm.toLowerCase()));
  };

  const sanitizeCSVValue = (v) => {
    if (!v) return '';
    let s = v.toString().replace(/"/g, '""');
    if ('=+-@'.includes(s[0])) s = `'${s}`;
    return `"${s}"`;
  };

  const exportToCSV = () => {
    const rows = filterData();
    if (!rows.length) return;
    const headers = Object.keys(rows[0]).join(',');
    const body = rows.map(r => Object.values(r).map(v => sanitizeCSVValue(v)).join(',')).join('\n');
    const csv = `data:text/csv;charset=utf-8,${headers}\n${body}`;
    const a = document.createElement('a');
    a.setAttribute('href', encodeURI(csv));
    a.setAttribute('download', `${activeTab}-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const filteredData = filterData();

  /* ─── LOGIN SCREEN ─── */
  if (!isAuthenticated) {
    return (
      <div style={S.loginWrap}>
        {/* Background decoration */}
        <div style={{ position: 'absolute', top: '-100px', right: '-100px', width: 500, height: 500, borderRadius: '50%', background: 'rgba(251,191,36,0.07)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-80px', left: '-80px', width: 400, height: 400, borderRadius: '50%', background: 'rgba(154,219,2,0.05)', pointerEvents: 'none' }} />

        <div style={S.loginCard}>
          {/* Logo + title */}
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{ width: 64, height: 64, background: 'linear-gradient(135deg, #1E3A8A, #4169E1)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', boxShadow: '0 8px 24px rgba(65,105,225,0.35)' }}>
              <FaShieldAlt style={{ color: '#FBBF24', fontSize: 26 }} />
            </div>
            <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: 24, fontWeight: 900, color: '#1E3A8A', marginBottom: 4 }}>
              {t('admin.loginTitle', 'Admin Portal')}
            </h2>
            <p style={{ fontSize: 12, color: '#9CA3AF', letterSpacing: '0.05em' }}>San Tomas Tourism College</p>
          </div>

          {/* Lockout banner */}
          {isLocked && lockTime && (
            <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '12px 16px', marginBottom: 20, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <FaExclamationTriangle style={{ color: '#D97706', flexShrink: 0, marginTop: 2 }} />
              <p style={{ fontSize: 13, color: '#92400E', margin: 0 }}>
                {t('admin.lockedOut', { minutes: Math.ceil((lockTime - Date.now()) / 1000 / 60), defaultValue: 'Too many failed attempts. Please try again later.' })}
              </p>
            </div>
          )}

          {/* Error banner */}
          {passwordError && !isLocked && (
            <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '12px 16px', marginBottom: 20, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <FaExclamationTriangle style={{ color: '#EF4444', flexShrink: 0, marginTop: 2 }} />
              <p style={{ fontSize: 13, color: '#B91C1C', margin: 0 }}>{passwordError}</p>
            </div>
          )}

          <form onSubmit={handlePasswordSubmit}>
            {/* Username */}
            <div style={{ marginBottom: 16 }}>
              <label htmlFor="admin-username" style={S.inputLabel}>{t('admin.username','Username')}</label>
              <div style={S.inputWrap}>
                <FaUser style={S.inputIcon} />
                <input
                  type="text" id="admin-username" value={username}
                  onChange={e => setUsername(e.target.value)}
                  style={S.inputField}
                  placeholder={t('admin.usernamePlaceholder','Enter username')}
                  disabled={isLocked} autoComplete="username" ref={usernameInputRef}
                  onFocus={e => { e.target.style.borderColor = '#4169E1'; e.target.style.boxShadow = '0 0 0 3px rgba(65,105,225,0.12)'; }}
                  onBlur={e => { e.target.style.borderColor = 'rgba(65,105,225,0.2)'; e.target.style.boxShadow = 'none'; }}
                />
              </div>
            </div>

            {/* Password */}
            <div style={{ marginBottom: 24 }}>
              <label htmlFor="admin-password" style={S.inputLabel}>{t('admin.passwordLabel','Password')}</label>
              <div style={S.inputWrap}>
                <FaLock style={S.inputIcon} />
                <input
                  type="password" id="admin-password" value={password}
                  onChange={e => setPassword(e.target.value)}
                  style={S.inputField}
                  placeholder={t('admin.passwordPlaceholder','Enter password')}
                  disabled={isLocked} autoComplete="current-password"
                  onFocus={e => { e.target.style.borderColor = '#4169E1'; e.target.style.boxShadow = '0 0 0 3px rgba(65,105,225,0.12)'; }}
                  onBlur={e => { e.target.style.borderColor = 'rgba(65,105,225,0.2)'; e.target.style.boxShadow = 'none'; }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <button type="submit" disabled={isLocked}
                className={isLocked ? '' : 'btn-primary'}
                style={{ flex: 1, justifyContent: 'center', border: 'none', cursor: isLocked ? 'not-allowed' : 'pointer', fontSize: 13, padding: '12px', borderRadius: 50, fontFamily: "'Poppins',sans-serif", fontWeight: 700, background: isLocked ? '#E5E7EB' : undefined, color: isLocked ? '#9CA3AF' : undefined }}>
                {t('admin.loginButton','Login')}
              </button>
              <button type="button" onClick={() => navigate('/')} disabled={isLocked}
                style={{ flex: 1, padding: '12px', borderRadius: 50, border: '1.5px solid rgba(65,105,225,0.3)', background: 'transparent', cursor: isLocked ? 'not-allowed' : 'pointer', fontFamily: "'Poppins',sans-serif", fontSize: 13, fontWeight: 600, color: '#6B7280', transition: 'all 0.3s' }}>
                {t('admin.cancelButton','Cancel')}
              </button>
            </div>
          </form>

          <div style={{ marginTop: 24, textAlign: 'center' }}>
            <p style={{ fontSize: 12, color: '#9CA3AF' }}>{t('admin.loginTips','Please log out after each session.')}</p>
            {loginAttempts > 0 && !isLocked && (
              <p style={{ marginTop: 8, fontSize: 12, color: '#D97706', fontWeight: 600 }}>
                {t('admin.attempts', { attempts: loginAttempts, max: MAX_ATTEMPTS, defaultValue: `Attempts: ${loginAttempts}/${MAX_ATTEMPTS}` })}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  /* ─── DASHBOARD ─── */
  const statsArr = [
    { icon: <FaFileAlt />,      label: 'Enrollment Applications', value: activeTab === 'enrollment'       ? data.length : '—', color: '#4169E1' },
    { icon: <FaEnvelope />,     label: 'Express Interest',         value: activeTab === 'express-interest' ? data.length : '—', color: '#D97706' },
    { icon: <FaUserGraduate />, label: 'Alumni Subscribers',       value: activeTab === 'alumni-newsletter'? data.length : '—', color: '#5a9200' },
    { icon: <FaSearch />,       label: 'Showing (filtered)',        value: filteredData.length,                                  color: '#7C3AED' },
  ];

  return (
    <div style={S.page}>
      <Helmet>
        <title>{t('admin.title','Admin Dashboard - San Tomas Tourism College')}</title>
        <meta name="robots" content="noindex, nofollow" />
        <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&family=Fraunces:ital,wght@0,700;0,900;1,700;1,900&display=swap" rel="stylesheet" />
        {/* Without this, Chrome and Edge may auto-darken the page and invert
            backgrounds while leaving inline text colours alone — which is one
            way these tables ended up as dark grey text on near-black. */}
        <meta name="color-scheme" content="light" />
      </Helmet>

      {/* ── Session Warning Modal ── */}
      {showSessionWarning && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: '40px 36px', maxWidth: 400, width: '90%', textAlign: 'center', boxShadow: '0 32px 80px rgba(0,0,0,0.3)' }}>
            <div style={{ width: 56, height: 56, background: 'rgba(251,191,36,0.15)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
              <FaExclamationTriangle style={{ color: '#D97706', fontSize: 24 }} />
            </div>
            <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: 22, fontWeight: 900, color: '#1E3A8A', marginBottom: 8 }}>
              {t('admin.sessionWarningTitle','Session About to Expire')}
            </h2>
            <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 28, lineHeight: 1.7 }}>
              {t('admin.sessionWarningMessage','Your session will expire due to inactivity soon.')}
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button onClick={resetInactivityTimer} className="btn-primary" style={{ border: 'none', cursor: 'pointer', fontSize: 12 }}>
                {t('admin.stayLoggedIn','Stay Logged In')}
              </button>
              <button onClick={handleLogout} className="btn-outline" style={{ cursor: 'pointer', fontSize: 12 }}>
                {t('admin.logout','Logout')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Dashboard Header ── */}
      <div style={S.dashHeader}>
        <div style={{ maxWidth: 1280, margin: '0 auto', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
              <img src="/logo.png" alt="STTC" style={{ width: 44, height: 44, objectFit: 'contain' }} />
              <div>
                <h1 style={{ fontFamily: "'Fraunces',serif", fontSize: 24, fontWeight: 900, color: '#fff', margin: 0 }}>
                  {t('admin.dashboard','Admin Dashboard')}
                </h1>
                <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', margin: 0 }}>
                  {t('admin.subtitle','Manage college applications and inquiries')}
                </p>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', background: 'rgba(255,255,255,0.1)', padding: '6px 14px', borderRadius: 50, display: 'flex', alignItems: 'center', gap: 6 }}>
              ⏰ {currentTime || '—'}
            </div>
            <Link to="/"
              style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, fontWeight: 600, color: '#fff', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 50, padding: '8px 16px', textDecoration: 'none', transition: 'all 0.3s' }}>
              <FaArrowLeft style={{ fontSize: 10 }} /> {t('admin.returnHome','Home')}
            </Link>
            <button onClick={() => setRefreshCounter(p => p + 1)}
              style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, fontWeight: 600, color: '#fff', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 50, padding: '8px 16px', cursor: 'pointer', transition: 'all 0.3s' }}>
              <FaSync style={{ fontSize: 10 }} /> {t('admin.refresh','Refresh')}
            </button>
            <button onClick={handleLogout}
              style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, fontWeight: 600, color: '#1E3A8A', background: '#FBBF24', border: 'none', borderRadius: 50, padding: '8px 16px', cursor: 'pointer', transition: 'all 0.3s' }}>
              <FaSignOutAlt style={{ fontSize: 10 }} /> {t('admin.logout','Logout')}
            </button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '32px 24px 64px' }}>
        {/* Stats row */}
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 28 }}>
          {statsArr.map((s, i) => <StatCard key={i} {...s} />)}
        </div>

        {/* Main card */}
        <div style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', border: '1px solid rgba(65,105,225,0.1)', boxShadow: '0 4px 24px rgba(65,105,225,0.06)' }}>
          {/* Tab bar */}
          <div style={S.tabBar}>
            {tabs.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={S.tabBtn(activeTab === tab.id)}>
                {tab.icon} {tab.label}
                {activeTab === tab.id && data.length > 0 && (
                  <span style={{ fontSize: 10, background: '#FBBF24', color: '#1E3A8A', borderRadius: 50, padding: '2px 7px', fontWeight: 700, marginLeft: 4 }}>
                    {data.length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Toolbar */}
          <div style={{ padding: '20px 24px', borderBottom: '1px solid rgba(65,105,225,0.08)', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 14 }}>
            <div>
              <div style={{ fontFamily: "'Fraunces',serif", fontSize: 18, fontWeight: 900, color: '#1E3A8A' }}>
                {tabs.find(t => t.id === activeTab)?.label}
              </div>
              <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 3 }}>
                {t('admin.count', { count: filteredData.length, defaultValue: `Showing ${filteredData.length} of ${data.length} entries` })}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {/* Search */}
              <div style={{ position: 'relative' }}>
                <FaSearch style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF', fontSize: 12 }} />
                <input
                  type="text"
                  placeholder={t('admin.searchPlaceholder','Search...')}
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="admin-search"
                  aria-label="Search records"
                />
              </div>
              {/* Export */}
              <button onClick={exportToCSV} disabled={filteredData.length === 0}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 18px', borderRadius: 50, border: 'none', fontFamily: "'Poppins',sans-serif", fontSize: 12, fontWeight: 600, cursor: filteredData.length === 0 ? 'not-allowed' : 'pointer', background: filteredData.length === 0 ? '#E5E7EB' : '#9ADB02', color: filteredData.length === 0 ? '#9CA3AF' : '#1a4d00', transition: 'all 0.3s' }}>
                <FaDownload style={{ fontSize: 11 }} /> {t('admin.export','Export CSV')}
              </button>
            </div>
          </div>

          {/* A document failed to open — most often because the file was lost
              to the ephemeral filesystem before UPLOAD_DIR was configured. */}
          {docError && (
            <div style={{ margin: '0 0 12px', padding: '12px 16px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <FaExclamationTriangle style={{ fontSize: 14, color: '#B91C1C', marginTop: 2, flexShrink: 0 }} />
              <p style={{ fontSize: 12.5, color: '#B91C1C', margin: 0, lineHeight: 1.6, flex: 1 }}>{docError}</p>
              <button type="button" onClick={() => setDocError(null)}
                style={{ background: 'none', border: 0, color: '#B91C1C', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 0 }}
                aria-label="Dismiss">&times;</button>
            </div>
          )}

          {/* Table area */}
          <div style={{ padding: '8px 0' }}>
            {loading ? (
              <div style={{ padding: '80px 24px', textAlign: 'center' }}>
                <div style={{ width: 48, height: 48, borderRadius: '50%', border: '3px solid rgba(65,105,225,0.15)', borderTopColor: '#4169E1', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
                <p style={{ fontSize: 14, color: '#9CA3AF' }}>{t('admin.loading','Loading data...')}</p>
              </div>
            ) : error ? (
              <div style={{ padding: '60px 24px', textAlign: 'center' }}>
                <FaExclamationTriangle style={{ fontSize: 40, color: '#EF4444', marginBottom: 16 }} />
                <h3 style={{ fontSize: 18, color: '#B91C1C', marginBottom: 8 }}>{t('admin.errorTitle','Failed to Load Data')}</h3>
                <p style={{ fontSize: 13, color: '#EF4444', marginBottom: 20, maxWidth: 400, margin: '0 auto 20px' }}>{error}</p>
                <button onClick={() => setRefreshCounter(p => p + 1)} className="btn-royal" style={{ border: 'none', cursor: 'pointer', fontSize: 12 }}>
                  <FaSync /> {t('admin.retry','Try Again')}
                </button>
              </div>
            ) : data.length === 0 ? (
              <div style={{ padding: '80px 24px', textAlign: 'center' }}>
                <div style={{ fontSize: 52, marginBottom: 16 }}>📭</div>
                <h3 style={{ fontFamily: "'Fraunces',serif", fontSize: 20, color: '#6B7280', marginBottom: 8 }}>{t('admin.noDataTitle','No Data Available')}</h3>
                <p style={{ fontSize: 13, color: '#9CA3AF', marginBottom: 20 }}>{t('admin.noDataMessage','There are currently no entries in this section.')}</p>
                <button onClick={() => setRefreshCounter(p => p + 1)} className="btn-outline" style={{ cursor: 'pointer', fontSize: 12 }}>
                  <FaSync /> {t('admin.refresh','Refresh')}
                </button>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                {/* ── ENROLLMENT TABLE ── */}
                {/* The navy gradient that used to sit on this table showed
                    through every unstyled row. Cells carry their own
                    background now. */}
                {activeTab === 'enrollment' && (
                  <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse', background: '#FFFFFF', borderRadius: 0 }}>
                    <thead>
                      <tr>
                        {[t('admin.name','Name'), t('admin.email','Email'), t('admin.telephone','Phone'), t('admin.program','Program'), t('admin.date','Submitted'), 'Attachments'].map(h => (
                          <th key={h} style={S.th}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredData.map((item, i) => {
                        const hov = hoveredRow === `en-${item.id}`;
                        return (
                        <tr key={item.id}
                          onMouseEnter={() => setHoveredRow(`en-${item.id}`)}
                          onMouseLeave={() => setHoveredRow(null)}>
                          <td style={cell(S, i, hov)}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg, #4169E1, #1E3A8A)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                                {(item.full_name || '?')[0].toUpperCase()}
                              </div>
                              <span style={{ fontWeight: 600 }}>{item.full_name}</span>
                            </div>
                          </td>
                          <td style={cell(S, i, hov)}>{item.email_address}</td>
                          <td style={cell(S, i, hov)}>{item.phone_number}</td>
                          <td style={cell(S, i, hov)}>
                            <span style={S.badge('#4169E1')}>{item.preferred_program}</span>
                          </td>
                          <td style={cell(S, i, hov, { color: '#4B5563', fontSize: 12 })}>{formatDate(item.submitted_at)}</td>
                          <td style={cell(S, i, hov)}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              {[
                                { key: 'id_passport_copy', label: 'ID/Passport' },
                                { key: 'kcse_certificate', label: 'KCSE Cert' },
                                { key: 'certificates_degrees', label: 'Other Certs' },
                              ].map(f => (
                                item[f.key] && item[f.key] !== 'N/A' ? (
                                  <button key={f.key}
                                    type="button"
                                    onClick={() => openDocument(item[f.key], f.label)}
                                    disabled={docBusy === item[f.key]}
                                    title={`Open ${f.label}`}
                                    style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#3355C4', fontWeight: 600, background: 'none', border: 0, padding: 0, cursor: docBusy === item[f.key] ? 'wait' : 'pointer', font: 'inherit', textAlign: 'left' }}>
                                    <FaPaperclip style={{ fontSize: 9 }} />
                                    {docBusy === item[f.key] ? 'Opening…' : f.label}
                                  </button>
                                ) : (
                                  <span key={f.key} style={{ fontSize: 11, color: '#4B5563' }}>{f.label}: —</span>
                                )
                              ))}
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}

                {/* ── EXPRESS INTEREST TABLE ── */}
                {activeTab === 'express-interest' && (
                  <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse', background: '#FFFFFF' }}>
                    <thead>
                      <tr>
                        {[t('admin.name','Name'), t('admin.email','Email'), t('admin.telephone','Telephone'), t('admin.date','Date Submitted')].map(h => (
                          <th key={h} style={S.th}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredData.map((item, i) => {
                        const hov = hoveredRow === `ei-${item.id}`;
                        return (
                          <tr key={item.id}
                            onMouseEnter={() => setHoveredRow(`ei-${item.id}`)}
                            onMouseLeave={() => setHoveredRow(null)}>
                            <td style={cell(S, i, hov)}><span style={{ fontWeight: 600 }}>{item.name}</span></td>
                            <td style={cell(S, i, hov)}>{item.email}</td>
                            <td style={cell(S, i, hov)}>{item.telephone}</td>
                            <td style={cell(S, i, hov, { color: '#4B5563', fontSize: 12 })}>{formatDate(item.submission_date)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}

                {/* ── ALUMNI NEWSLETTER TABLE ── */}
                {activeTab === 'alumni-newsletter' && (
                  <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse', background: '#FFFFFF' }}>
                    <thead>
                      <tr>
                        {[t('admin.email','Email'), t('admin.name','Name'), t('admin.graduation','Graduation Year'), t('admin.date','Subscribed At')].map(h => (
                          <th key={h} style={S.th}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredData.map((item, i) => {
                        const hov = hoveredRow === `al-${item.id}`;
                        return (
                          <tr key={item.id}
                            onMouseEnter={() => setHoveredRow(`al-${item.id}`)}
                            onMouseLeave={() => setHoveredRow(null)}>
                            <td style={cell(S, i, hov)}><span style={{ fontWeight: 600 }}>{item.email}</span></td>
                            <td style={cell(S, i, hov)}>{item.name || <span style={{ color: '#4B5563' }}>—</span>}</td>
                            <td style={cell(S, i, hov)}>{item.graduation_year ? <span style={S.badge('#5a9200')}>{item.graduation_year}</span> : <span style={{ color: '#4B5563' }}>—</span>}</td>
                            <td style={cell(S, i, hov, { color: '#4B5563', fontSize: 12 })}>{formatDate(item.submitted_at)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}

                {/* Footer row */}
                <div style={{ padding: '12px 24px', borderTop: '1px solid rgba(65,105,225,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: '#9CA3AF' }}>
                    {filteredData.length} of {data.length} records
                  </span>
                  {filteredData.length === data.length && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#9ADB02', fontWeight: 600 }}>
                      <FaCheckCircle style={{ fontSize: 10 }} /> All records shown
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer note */}
        <div style={{ marginTop: 32, textAlign: 'center' }}>
          <p style={{ fontSize: 12, color: '#9CA3AF' }}>San Tomas Tourism College Admin Portal · {new Date().getFullYear()}</p>
          <p style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>{t('admin.accessMessage','Restricted access for authorised personnel only')}</p>
          <p style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>{t('admin.sessionInfo','Session expires after 15 minutes of inactivity')}</p>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default Admin;